import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/site/Logo";
import {
  Images, Upload, Trash2, Loader2, ArrowLeft, LayoutList, BarChart3, LogOut, X, ImagePlus,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_CATS = ["Bagni", "Pitture", "Cartongesso", "Muratura", "Pavimenti", "Pareti", "Altro"];
const MAX_MB = 10;

export default function GalleryAdmin() {
  const { logout } = useAuth();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState(FALLBACK_CATS);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [beforeFile, setBeforeFile] = useState(null);
  const [beforePreview, setBeforePreview] = useState(null);
  const [category, setCategory] = useState("Bagni");
  const [label, setLabel] = useState("");
  const inputRef = useRef(null);
  const beforeInputRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/gallery`);
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error("Errore nel caricamento della galleria.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    axios.get(`${API}/gallery/categories`).then((r) => {
      if (Array.isArray(r.data) && r.data.length) setCats(r.data);
    }).catch(() => {});
  }, []);

  const pickFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Seleziona un file immagine."); return; }
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`L'immagine supera i ${MAX_MB}MB.`); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const pickBefore = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Seleziona un file immagine."); return; }
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`L'immagine supera i ${MAX_MB}MB.`); return; }
    setBeforeFile(f);
    setBeforePreview(URL.createObjectURL(f));
  };

  const clearSelection = () => {
    setFile(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setBeforeFile(null);
    if (beforePreview) URL.revokeObjectURL(beforePreview);
    setBeforePreview(null);
    if (inputRef.current) inputRef.current.value = "";
    if (beforeInputRef.current) beforeInputRef.current.value = "";
  };

  const upload = async () => {
    if (!file) { toast.error("Seleziona prima un'immagine."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      fd.append("label", label);
      if (beforeFile) fd.append("before", beforeFile);
      const r = await axios.post(`${API}/gallery`, fd);
      setItems((prev) => [...prev, r.data]);
      toast.success(beforeFile ? "Prima/Dopo caricato." : "Immagine caricata.");
      clearSelection();
      setLabel("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante il caricamento.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/gallery/${id}`);
      setItems((prev) => prev.filter((g) => g.id !== id));
      toast.success("Immagine eliminata.");
    } catch (e) {
      toast.error("Errore durante l'eliminazione.");
    } finally {
      setConfirmId(null);
    }
  };

  return (
    <div className="min-h-screen bg-surface">
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-xl border-b border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-3">
            <Link to="/crm" data-testid="gallery-to-crm" className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-ink/70 hover:text-navy hover:border-navy/30 transition-colors">
              <LayoutList size={15} /> <span className="hidden sm:inline">Richieste</span>
            </Link>
            <Link to="/crm/dashboard" data-testid="gallery-to-dashboard" className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-ink/70 hover:text-navy hover:border-navy/30 transition-colors">
              <BarChart3 size={15} /> <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-ink/60 hover:text-navy">
              <ArrowLeft size={16} /> Sito
            </Link>
            <button onClick={logout} data-testid="gallery-logout" className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3.5 py-2 text-sm font-semibold text-ink/70 hover:text-red-600 hover:border-red-200 transition-colors">
              <LogOut size={15} /> <span className="hidden sm:inline">Esci</span>
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-brand-orange/15 text-brand-orange flex items-center justify-center"><Images size={22} /></div>
          <div>
            <h1 className="font-display font-extrabold text-3xl text-navy">Galleria lavori</h1>
            <p className="text-ink/55 mt-0.5 text-sm">Carica foto reali dei tuoi interventi. Vengono mostrate nella galleria del sito.</p>
          </div>
        </div>

        {/* Upload panel */}
        <div className="mt-6 rounded-2xl bg-white border border-gray-100 p-5 sm:p-6">
          <h2 className="font-display font-bold text-navy mb-4">Carica una nuova foto</h2>
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-5">
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
              data-testid="gallery-upload-zone"
              className="cursor-pointer rounded-xl border-2 border-dashed border-gray-200 hover:border-brand-orange/50 hover:bg-surface transition-colors p-4 text-center flex flex-col items-center justify-center min-h-[180px]"
            >
              {preview ? (
                <div className="relative">
                  <img src={preview} alt="Anteprima" className="max-h-40 rounded-lg object-cover" />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); clearSelection(); }}
                    data-testid="gallery-clear-selection"
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <>
                  <ImagePlus size={30} className="text-navy/40" />
                  <p className="mt-2 text-sm text-ink/60">Trascina qui o <span className="text-navy font-semibold">clicca per scegliere</span></p>
                  <p className="text-xs text-ink/40 mt-1">JPG, PNG, WEBP · max {MAX_MB}MB</p>
                </>
              )}
              <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid="gallery-file-input" onChange={(e) => pickFile(e.target.files?.[0])} />
            </div>

            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-semibold text-ink mb-1.5">Categoria</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  data-testid="gallery-category-select"
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-[15px] text-ink outline-none focus:border-navy focus:ring-2 focus:ring-navy/10"
                >
                  {cats.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-ink mb-1.5">Didascalia <span className="text-ink/40 font-normal">(facoltativa)</span></label>
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Es. Rifacimento bagno a Prati"
                  data-testid="gallery-label-input"
                  className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-[15px] text-ink placeholder:text-ink/40 outline-none focus:border-navy focus:ring-2 focus:ring-navy/10"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-ink mb-1.5">Foto "prima" <span className="text-ink/40 font-normal">(facoltativa · crea uno slider Prima/Dopo)</span></label>
                {beforePreview ? (
                  <div className="relative inline-block">
                    <img src={beforePreview} alt="Anteprima prima" className="h-20 rounded-lg object-cover border border-gray-200" />
                    <button type="button" onClick={() => { setBeforeFile(null); if (beforePreview) URL.revokeObjectURL(beforePreview); setBeforePreview(null); if (beforeInputRef.current) beforeInputRef.current.value=''; }} data-testid="gallery-clear-before" className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center"><X size={14} /></button>
                  </div>
                ) : (
                  <button type="button" onClick={() => beforeInputRef.current?.click()} data-testid="gallery-before-btn" className="inline-flex items-center gap-2 rounded-xl border border-dashed border-gray-300 px-4 py-2.5 text-sm font-semibold text-ink/70 hover:border-brand-orange/50 hover:text-navy transition-colors">
                    <ImagePlus size={16} /> Aggiungi foto "prima"
                  </button>
                )}
                <input ref={beforeInputRef} type="file" accept="image/*" className="hidden" data-testid="gallery-before-input" onChange={(e) => pickBefore(e.target.files?.[0])} />
              </div>
              <button
                onClick={upload}
                disabled={uploading || !file}
                data-testid="gallery-upload-btn"
                className="mt-auto inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3.5 text-[15px] font-semibold text-white shadow-[0_10px_28px_-8px_rgba(242,140,40,0.8)] hover:bg-brand-orange-dark transition-colors disabled:opacity-60"
              >
                {uploading ? <><Loader2 size={18} className="animate-spin" /> Caricamento...</> : <><Upload size={18} /> Carica foto</>}
              </button>
            </div>
          </div>
        </div>

        {/* Existing gallery */}
        <div className="mt-8 flex items-center justify-between">
          <h2 className="font-display font-bold text-navy">Foto pubblicate <span className="text-ink/40 font-normal" data-testid="gallery-count">({items.length})</span></h2>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-navy" /></div>
        ) : items.length === 0 ? (
          <div data-testid="gallery-empty" className="mt-4 rounded-2xl border border-dashed border-gray-200 bg-white py-14 text-center">
            <Images size={30} className="mx-auto text-navy/30" />
            <p className="mt-3 text-ink/60 text-sm">Nessuna foto caricata. Il sito mostra immagini di esempio finché non ne carichi di tue.</p>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((g) => (
              <div key={g.id} data-testid={`gallery-card-${g.id}`} className="group relative overflow-hidden rounded-xl border border-gray-100 bg-white">
                <div className="aspect-[4/3] overflow-hidden bg-surface">
                  <img src={`${BASE}${g.url}`} alt={g.label || g.category} loading="lazy" className="h-full w-full object-cover" />
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-brand-orange">{g.category}</span>
                    {g.before_url && <span data-testid={`gallery-badge-beforeafter-${g.id}`} className="rounded-full bg-navy/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-navy">Prima/Dopo</span>}
                  </div>
                  {g.label && <p className="text-sm text-ink/70 line-clamp-1">{g.label}</p>}
                </div>

                {confirmId === g.id ? (
                  <div className="absolute inset-0 bg-navy/85 flex flex-col items-center justify-center gap-3 p-4 text-center">
                    <p className="text-white text-sm font-medium">Eliminare questa foto?</p>
                    <div className="flex gap-2">
                      <button onClick={() => remove(g.id)} data-testid={`gallery-confirm-delete-${g.id}`} className="rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 transition-colors">Elimina</button>
                      <button onClick={() => setConfirmId(null)} className="rounded-full bg-white/20 px-4 py-2 text-sm font-semibold text-white hover:bg-white/30 transition-colors">Annulla</button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmId(g.id)}
                    data-testid={`gallery-delete-${g.id}`}
                    className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 text-red-600 flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                    aria-label="Elimina foto"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
